import toast, { Toaster, ToastOptions } from 'react-hot-toast';

// Custom toast configurations
const defaultOptions: ToastOptions = {
  duration: 4000,
  position: 'top-right',
  style: {
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    padding: '12px 16px',
    maxWidth: '400px',
  },
};

const successOptions: ToastOptions = {
  ...defaultOptions,
  icon: '✅',
  style: {
    ...defaultOptions.style,
    background: '#10B981',
    color: '#ffffff',
    border: '1px solid #059669',
  },
};

const errorOptions: ToastOptions = {
  ...defaultOptions,
  icon: '❌',
  duration: 6000, // Longer duration for errors
  style: {
    ...defaultOptions.style,
    background: '#EF4444',
    color: '#ffffff',
    border: '1px solid #DC2626',
  },
};

const warningOptions: ToastOptions = {
  ...defaultOptions,
  icon: '⚠️',
  style: {
    ...defaultOptions.style,
    background: '#F59E0B',
    color: '#ffffff',
    border: '1px solid #D97706',
  },
};

const infoOptions: ToastOptions = {
  ...defaultOptions,
  icon: 'ℹ️',
  style: {
    ...defaultOptions.style,
    background: '#3B82F6',
    color: '#ffffff',
    border: '1px solid #2563EB',
  },
};

const loadingOptions: ToastOptions = {
  ...defaultOptions,
  duration: Infinity, // Loading toast should be manually dismissed
  style: {
    ...defaultOptions.style,
    background: '#6B7280',
    color: '#ffffff',
    border: '1px solid #4B5563',
  },
};

export const toastService = {
  // Success notifications
  success: (message: string, options?: ToastOptions) => {
    return toast.success(message, { ...successOptions, ...options });
  },

  // Error notifications
  error: (message: string, options?: ToastOptions) => {
    return toast.error(message, { ...errorOptions, ...options });
  },

  // Warning notifications
  warning: (message: string, options?: ToastOptions) => {
    return toast(message, { ...warningOptions, ...options });
  },

  // Info notifications
  info: (message: string, options?: ToastOptions) => {
    return toast(message, { ...infoOptions, ...options });
  },

  // Loading notifications
  loading: (message: string, options?: ToastOptions) => {
    return toast.loading(message, { ...loadingOptions, ...options });
  },

  // Custom notifications
  custom: (message: string, options?: ToastOptions) => {
    return toast(message, { ...defaultOptions, ...options });
  },

  // Dismiss specific toast
  dismiss: (toastId?: string) => {
    return toast.dismiss(toastId);
  },

  // Dismiss all toasts
  dismissAll: () => {
    return toast.dismiss();
  },

  // Promise-based toast for async operations
  promise: <T>(
    promise: Promise<T>,
    messages: {
      loading: string;
      success: string | ((data: T) => string);
      error: string | ((error: any) => string);
    },
    options?: ToastOptions
  ) => {
    return toast.promise(
      promise,
      {
        loading: messages.loading,
        success: messages.success,
        error: messages.error,
      },
      { ...defaultOptions, ...options }
    );
  },

  // Admin specific notifications
  admin: {
    userCreated: (username: string) => 
      toastService.success(`Felhasználó létrehozva: ${username}`),
    
    userDeleted: (username: string) => 
      toastService.warning(`Felhasználó törölve: ${username}`),
    
    gameEnded: (gameId: string) => 
      toastService.info(`Játék befejezve: #${gameId.slice(-6)}`),
    
    coinTransaction: (amount: number, user: string) => 
      toastService.success(`${amount} aranytallér hozzáadva: ${user}`),
    
    systemError: (error: string) => 
      toastService.error(`Rendszerhiba: ${error}`),
    
    backup: (status: 'success' | 'failed') => 
      status === 'success' 
        ? toastService.success('Backup sikeresen elkészült')
        : toastService.error('Backup sikertelen'),
    
    maintenance: (enabled: boolean) => 
      enabled 
        ? toastService.warning('Karbantartási mód bekapcsolva')
        : toastService.info('Karbantartási mód kikapcsolva'),
    
    newNotification: (title: string) => 
      toastService.info(`Új értesítés: ${title}`),
  }
};

// Export the Toaster component for App.tsx
export { Toaster };

// Export toast directly for backward compatibility
export default toast; 